'use client'

import { saveCookieConsentPreferences } from '@/actions/ads-tracking'
import { cn } from '@/lib/utils'
import { Check, Cookie, X } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Button } from './ui/button'

export const COOKIE_CONSENT_CHANGED_EVENT = 'gf-cookie-consent-changed'
export const COOKIE_CONSENT_OPEN_EVENT = 'gf-cookie-consent-open'
export const COOKIE_CONSENT_STORAGE_KEY = 'gf_cookie_consent_v3'
const COOKIE_CONSENT_VERSION = 3

const DEFAULT_PREFERENCES = {
  necessary: true,
  analytics: false,
  advertising: false,
}

function parseStoredConsent(value) {
  if (!value) return null

  try {
    const parsed = JSON.parse(value)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.advertising !== 'boolean'
    ) {
      return null
    }

    return {
      version:
        typeof parsed.version === 'number'
          ? parsed.version
          : COOKIE_CONSENT_VERSION,
      necessary: true,
      analytics: parsed.analytics,
      advertising: parsed.advertising,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
    }
  } catch {
    return null
  }
}

function readLocalStorage(key) {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeLocalStorage(key, value) {
  if (typeof window === 'undefined') return false

  try {
    if (!window.localStorage) return false
    window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export function getCookieConsentPreferences() {
  return parseStoredConsent(readLocalStorage(COOKIE_CONSENT_STORAGE_KEY))
}

export function hasAdvertisingCookieConsent() {
  const consent = getCookieConsentPreferences()
  return consent?.advertising === true
}

export default function CookieConsentBanner() {
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false)
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const localConsent = getCookieConsentPreferences()

    if (localConsent) {
      setPreferences({
        necessary: true,
        analytics: localConsent.analytics,
        advertising: localConsent.advertising,
      })
      setIsBannerVisible(false)
      return
    }

    setIsBannerVisible(true)
  }, [])

  const persistPreferences = async (nextPreferences) => {
    const normalized = {
      necessary: true,
      analytics: Boolean(nextPreferences.analytics),
      advertising: Boolean(nextPreferences.advertising),
    }

    const payload = {
      ...normalized,
      version: COOKIE_CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
    }

    writeLocalStorage(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(payload))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new window.Event(COOKIE_CONSENT_CHANGED_EVENT))
    }

    setPreferences(normalized)
    setIsBannerVisible(false)
    setIsPreferencesOpen(false)

    setIsSaving(true)
    try {
      await saveCookieConsentPreferences(normalized)
    } catch {
      // Keep local state even if DB persistence fails.
    } finally {
      setIsSaving(false)
    }
  }

  const acceptAll = () =>
    persistPreferences({
      analytics: true,
      advertising: true,
    })

  const rejectNonEssential = () =>
    persistPreferences({
      analytics: false,
      advertising: false,
    })

  const saveCustomPreferences = () => persistPreferences(preferences)

  useEffect(() => {
    const openPreferences = () => setIsPreferencesOpen(true)
    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, openPreferences)
    return () => {
      window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, openPreferences)
    }
  }, [])

  return (
    <>
      {isBannerVisible && (
        <>
          <div
            className="fixed inset-x-3 bottom-3 z-50 rounded-lg border border-neutral-blue-600 bg-neutral-blue-800 p-2 shadow-2xl sm:hidden"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
            }}
          >
            <div className="flex items-center gap-1.5">
              <Cookie className="h-4 w-4 text-orange-400" />
              <p className="flex-1 truncate text-xs font-semibold text-white">
                Cookies
              </p>
              <Button
                variant="default"
                className="h-9 rounded-md bg-orange-500 px-3 text-xs text-white hover:bg-orange-600"
                onClick={acceptAll}
                disabled={isSaving}
              >
                Accept
              </Button>
              <Button
                variant="outline"
                className="h-9 rounded-md border-neutral-blue-500 bg-neutral-blue-900 px-3 text-xs text-slate-100 hover:bg-neutral-blue-700"
                onClick={() => setIsPreferencesOpen(true)}
                disabled={isSaving}
              >
                Manage
              </Button>
            </div>
          </div>

          <div className="pointer-events-none fixed inset-0 z-50 hidden items-end justify-end p-4 sm:flex sm:p-6">
            <div className="pointer-events-auto w-full max-w-[360px] rounded-lg border border-neutral-blue-600 bg-neutral-blue-800 p-4 shadow-2xl">
              <div className="mb-4 flex items-start gap-3">
                <div className="shrink-0 pt-1">
                  <Cookie className="h-5 w-5 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-h-4 font-semibold text-white">
                    Cookie Preferences
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-300">
                    We use necessary cookies to run Grid Finder. Optional
                    analytics and advertising cookies help us improve and fund
                    the platform.{' '}
                    <Link
                      className="font-medium text-white underline underline-offset-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      href="/support/cookie-policy"
                      prefetch={false}
                    >
                      Cookie Policy
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 text-p-1 font-medium">
                <Button
                  variant="default"
                  className="w-full rounded-md bg-orange-500 text-white hover:bg-orange-600"
                  onClick={acceptAll}
                  disabled={isSaving}
                >
                  Accept All
                </Button>
                <Button
                  variant="outline"
                  className="w-full rounded-md border-neutral-blue-500 bg-neutral-blue-900 text-slate-100 hover:bg-neutral-blue-700"
                  onClick={rejectNonEssential}
                  disabled={isSaving}
                >
                  Reject Non-Essential
                </Button>
                <button
                  type="button"
                  className="ml-auto mt-1 text-sm text-slate-200 underline underline-offset-4 hover:text-white"
                  onClick={() => setIsPreferencesOpen(true)}
                >
                  Manage Preferences
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {isPreferencesOpen && (
        <div className="z-60 fixed inset-0 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-xl rounded-lg border border-white/20 bg-[#081327] p-6 text-white shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h4 className="text-xl font-semibold text-white">
                  Manage Cookie Preferences
                </h4>
                <p className="mt-1 text-sm text-slate-300">
                  Choose which optional cookies Grid Finder can use.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPreferencesOpen(false)}
                className="rounded-md p-1 text-slate-300 hover:bg-slate-700/60 hover:text-white"
                aria-label="Close cookie preferences"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <PreferenceRow
                label="Necessary"
                description="Required for login, security, and essential site functionality."
                checked
                disabled
              />
              <PreferenceRow
                label="Analytics"
                description="Helps us understand usage patterns and improve the product."
                checked={preferences.analytics}
                onChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, analytics: checked }))
                }
              />
              <PreferenceRow
                label="Advertising"
                description="Allows ad impression/click measurement and ad relevance."
                checked={preferences.advertising}
                onChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, advertising: checked }))
                }
              />
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={rejectNonEssential}
                disabled={isSaving}
              >
                Reject Non-Essential
              </Button>
              <Button onClick={saveCustomPreferences} disabled={isSaving}>
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function PreferenceRow({
  label,
  description,
  checked,
  disabled = false,
  onChange,
}) {
  return (
    <div className="rounded-lg border border-slate-600/50 bg-[#0d1a33] p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-white">{label}</p>
          <p className="mt-1 text-sm text-slate-300">{description}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`${label} cookies`}
          disabled={disabled}
          onClick={() => onChange?.(!checked)}
          className={cn(
            'inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors',
            checked
              ? 'border-emerald-600 bg-emerald-600'
              : 'border-gray-300 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
            disabled && 'cursor-not-allowed opacity-70',
          )}
        >
          <span
            className={cn(
              'mx-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-emerald-600 transition-transform',
              checked ? 'translate-x-5' : 'translate-x-0',
            )}
          >
            {checked ? <Check className="h-3.5 w-3.5" /> : null}
          </span>
        </button>
      </div>
    </div>
  )
}
